import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, MapPin, Phone, Instagram, Facebook, Linkedin, Youtube, Twitter } from 'lucide-react'
import { trackEvent } from '../../analytics'
import { useTheme } from '../../context/ThemeContext'
import { useSiteSettings } from '../../context/SiteSettingsContext'
import { SafeImage } from '../ui/SafeImage'
import { cityPages } from '../../data/cityPages'

const footerLinks = [
  { label: 'Home', path: '/' },
  { label: 'About', path: '/about' },
  { label: 'Services', path: '/services' },
  { label: 'Case Studies', path: '/case-study' },
  { label: 'Clients', path: '/clients' },
  { label: 'Knowledge & Insights', path: '/insights' },
  { label: 'Contact', path: '/contact' },
]

const legalLinks = [
  { label: 'Privacy Policy', path: '/privacy' },
  { label: 'Terms & Conditions', path: '/terms' },
  { label: 'Refund Policy', path: '/refund' },
]

export function Footer() {
  const { socialLinks } = useSiteSettings()
  const { theme } = useTheme()
  const logoSrc = theme === 'light' ? '/logo-light.png' : '/logo-dark.png'
  const { settings } = useSiteSettings()

  const email = settings?.email || 'info@digisutra.in'
  const phone = settings?.phone || '+91 8200670752'
  const address = [settings?.address_line1, settings?.address_line2, settings?.city, settings?.state, settings?.postal_code]
    .filter(Boolean)
    .join(', ') ||
    '812-814, Western Business Park, Opp. SD Jain School, Vesu, Surat, Gujarat, 395007'

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      className="bg-slate-100/80 dark:bg-navy-900/90 border-t border-slate-200 dark:border-white/5 backdrop-blur-md"
    >
      <div className="container-wide py-12 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          <div className="lg:col-span-4">
            <Link to="/" className="inline-block mb-4">
              <SafeImage
                src={logoSrc}
                alt="DigiSutra – Best Digital Marketing Agency in Surat"
                label="DigiSutra"
                tone="brand"
                width={160}
                height={56}
                loading="lazy"
                className="h-12 w-auto min-w-[7rem] object-contain sm:h-14"
              />
            </Link>
            <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base max-w-md mb-6">
              {settings?.tagline ||
                'Growth-focused digital agency. We build structured marketing systems that turn attention into revenue.'}
            </p>
            <div className="space-y-2 text-sm text-slate-600 dark:text-slate-400">
              <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-primary dark:hover:text-accent transition-colors">
                <Mail size={16} /> {email}
              </a>
              <a href={`tel:${phone.replace(/\s+/g, '')}`} className="flex items-center gap-2 hover:text-primary dark:hover:text-accent transition-colors">
                <Phone size={16} /> {phone}
              </a>
              <p className="flex items-start gap-2">
                <MapPin size={16} className="mt-0.5 shrink-0" />
                {address}
              </p>
            </div>
            {socialLinks.length > 0 && (
              <div className="mt-5 flex items-center gap-3">
                {socialLinks.map((s) => {
                  const p = (s.platform || '').toLowerCase()
                  const Icon =
                    p === 'instagram'
                      ? Instagram
                      : p === 'facebook'
                        ? Facebook
                        : p === 'linkedin'
                          ? Linkedin
                          : p === 'youtube'
                            ? Youtube
                            : p === 'x' || p === 'twitter'
                              ? Twitter
                              : null
                  if (!Icon) return null
                  return (
                    <a
                      key={s.id}
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.platform}
                      className="w-10 h-10 rounded-2xl bg-white/60 dark:bg-white/5 border border-slate-200/70 dark:border-white/10 flex items-center justify-center text-slate-700 dark:text-slate-200 hover:text-primary dark:hover:text-accent hover:border-primary/40 dark:hover:border-accent/40 transition-colors"
                      onClick={() => trackEvent('click', { link_url: s.url, link_text: `social_${p}`, outbound: true })}
                    >
                      <Icon size={18} strokeWidth={1.9} />
                    </a>
                  )
                })}
              </div>
            )}
          </div>
          <div className="lg:col-span-3">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4 text-sm uppercase tracking-wider">
              Quick links
            </h3>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-accent transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-2">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4 text-sm uppercase tracking-wider">
              Legal
            </h3>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-accent transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-3">
            <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4 text-sm uppercase tracking-wider">
              Get in touch
            </h3>
            <Link to="/contact" className="inline-flex btn-primary text-sm px-5 py-2.5" onClick={() => trackEvent('cta_click', { cta_name: 'book_strategy_call', from: 'footer' })}>
              Book Strategy Call
            </Link>
          </div>
        </div>

        {/* Cities We Serve — full width, multi-column so the list stays compact */}
        <div className="mt-10 pt-8 border-t border-slate-200/60 dark:border-white/10">
          <h3 className="font-semibold text-slate-800 dark:text-slate-100 mb-4 text-sm uppercase tracking-wider">
            Cities We Serve
          </h3>
          <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-6 gap-y-2.5">
            {cityPages.map((city) => (
              <li key={city.slug}>
                <Link
                  to={`/${city.slug}`}
                  className="text-slate-600 dark:text-slate-400 hover:text-primary dark:hover:text-accent transition-colors text-sm"
                >
                  {city.cityName}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-12 pt-8 border-t border-slate-200/60 dark:border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6 text-slate-500 dark:text-slate-500 text-sm">
            <p>© {new Date().getFullYear()} DigiSutra. All rights reserved.</p>
            <span className="hidden sm:inline">•</span>
            <p>
              Designed & Developed by{' '}
<a 
                href="https://jaimik.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary dark:text-accent font-medium hover:underline"
                onClick={() => trackEvent('click', { link_url: 'https://jaimik.com', link_text: 'jaimik.com', outbound: true })}
              >
                jaimik.com
              </a>
            </p>
          </div>
          <div className="flex flex-wrap gap-4 sm:gap-6 justify-center md:justify-end">
            <Link to="/privacy" className="text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-sm transition-colors">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-sm transition-colors">
              Terms & Conditions
            </Link>
            <Link to="/refund" className="text-slate-500 dark:text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 text-sm transition-colors">
              Refund Policy
            </Link>
          </div>
        </div>
        <div className="mt-8 pt-6 border-t border-slate-200/40 dark:border-white/5">
          <p className="text-slate-500 dark:text-slate-500 text-xs leading-relaxed text-center">
            DigiSutra — Best Digital Marketing Agency in Surat, Gujarat. We provide Social Media Marketing,
            Meta Ads, SEO, Lead Generation, Funnel Building, and Website Development services for B2B
            businesses, Real Estate developers, and Marble sellers. Serving Surat, Mumbai, Navi Mumbai,
            Bangalore, Hyderabad, Silvassa, Vapi, Valsad, Ahmedabad, Gandhinagar, and Vadodara.
            360 Digital Marketing Agency | B2B Marketing Agency | Lead Generation Agency Surat.
          </p>
        </div>
      </div>
    </motion.footer>
  )
}
